import React, { useMemo } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Select,
  MenuItem,
  Slider,
  Typography,
  Divider,
  Stack,
} from '@mui/material';
import { PeopleFilters as PeopleFiltersType } from '../../hooks/usePeopleFilters';
import { Person } from '../../types';

/**
 * PeopleFilters Component
 *
 * Form controls for filtering the people list
 *
 * Features:
 * - Gender filter (Male, Female, Other)
 * - Living Status filter (Living, Deceased, Both)
 * - Birth Year Range slider
 * - Has Photo toggle
 * - Has Biography toggle
 * - Last Name dropdown (populated from persons array)
 *
 * @component
 * @param {PeopleFiltersProps} props - Component props
 * @returns {JSX.Element} Filter controls
 */
interface PeopleFiltersProps {
  filters: PeopleFiltersType;
  onChange: (filters: PeopleFiltersType) => void;
  persons: Person[];
}

export const PeopleFilters: React.FC<PeopleFiltersProps> = ({
  filters,
  onChange,
  persons,
}) => {
  // ===== Computed Values =====
  const uniqueLastNames = useMemo(() => {
    const names = new Set<string>();
    persons.forEach((person) => {
      if (person.lastName) {
        names.add(person.lastName);
      }
    });
    return Array.from(names).sort();
  }, [persons]);

  const birthYearRange = useMemo(() => {
    const years = persons
      .filter((p) => p.birthDate)
      .map((p) => new Date(p.birthDate!).getFullYear())
      .filter((year) => !isNaN(year));

    if (years.length === 0) {
      return { min: 1900, max: new Date().getFullYear() };
    }

    return {
      min: Math.min(...years),
      max: Math.max(...years),
    };
  }, [persons]);

  const currentBirthYearRange: [number, number] = [
    filters.birthYearFrom ?? birthYearRange.min,
    filters.birthYearTo ?? birthYearRange.max,
  ];

  // ===== Event Handlers =====
  const handleGenderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    onChange({
      ...filters,
      gender: value === 'all' ? undefined : (value as 'male' | 'female' | 'other'),
    });
  };

  const handleLivingStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    onChange({
      ...filters,
      isDeceased: value === 'all' ? undefined : value === 'deceased',
    });
  };

  const handleBirthYearChange = (event: Event, newValue: number | number[]) => {
    const [from, to] = newValue as number[];
    onChange({
      ...filters,
      birthYearFrom: from === birthYearRange.min ? undefined : from,
      birthYearTo: to === birthYearRange.max ? undefined : to,
    });
  };

  const handleHasPhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...filters,
      hasPhoto: event.target.checked ? true : undefined,
    });
  };

  const handleHasBiographyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...filters,
      hasBiography: event.target.checked ? true : undefined,
    });
  };

  const handleLastNameChange = (event: any) => {
    const value = event.target.value;
    onChange({
      ...filters,
      lastName: value === 'all' ? undefined : value,
    });
  };

  // ===== Render =====
  return (
    <Stack spacing={3}>
      {/* Gender Filter */}
      <Box>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">Gender</FormLabel>
          <RadioGroup
            value={filters.gender || 'all'}
            onChange={handleGenderChange}
          >
            <FormControlLabel value="all" control={<Radio />} label="All" />
            <FormControlLabel value="male" control={<Radio />} label="Male" />
            <FormControlLabel value="female" control={<Radio />} label="Female" />
            <FormControlLabel value="other" control={<Radio />} label="Other" />
          </RadioGroup>
        </FormControl>
      </Box>

      <Divider />

      {/* Living Status Filter */}
      <Box>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">Status</FormLabel>
          <RadioGroup
            value={
              filters.isDeceased === undefined
                ? 'all'
                : filters.isDeceased
                ? 'deceased'
                : 'living'
            }
            onChange={handleLivingStatusChange}
          >
            <FormControlLabel value="all" control={<Radio />} label="All" />
            <FormControlLabel value="living" control={<Radio />} label="Living" />
            <FormControlLabel value="deceased" control={<Radio />} label="Deceased" />
          </RadioGroup>
        </FormControl>
      </Box>

      <Divider />

      {/* Birth Year Range Filter */}
      <Box>
        <FormLabel component="legend">Birth Year Range</FormLabel>
        <Box sx={{ px: 2, pt: 2 }}>
          <Slider
            value={currentBirthYearRange}
            onChange={handleBirthYearChange}
            valueLabelDisplay="auto"
            min={birthYearRange.min}
            max={birthYearRange.max}
            marks={[
              { value: birthYearRange.min, label: birthYearRange.min.toString() },
              { value: birthYearRange.max, label: birthYearRange.max.toString() },
            ]}
          />
          <Typography variant="body2" color="text.secondary" align="center">
            {currentBirthYearRange[0]} - {currentBirthYearRange[1]}
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* Last Name Filter */}
      <Box>
        <FormControl fullWidth>
          <FormLabel>Last Name</FormLabel>
          <Select
            value={filters.lastName || 'all'}
            onChange={handleLastNameChange}
            size="small"
          >
            <MenuItem value="all">All</MenuItem>
            {uniqueLastNames.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Divider />

      {/* Additional Filters */}
      <Box>
        <FormLabel component="legend">Additional Filters</FormLabel>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={filters.hasPhoto === true}
                onChange={handleHasPhotoChange}
              />
            }
            label="Has Photo"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filters.hasBiography === true}
                onChange={handleHasBiographyChange}
              />
            }
            label="Has Biography"
          />
        </FormGroup>
      </Box>
    </Stack>
  );
};

export default PeopleFilters;
